<template>
  <div class="custom-input">
    <label v-if="$validate.DataValid(label)" class="input-label">{{ $t(label) }}</label>
    <v-tooltip top :disabled="!enableTooltip">
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          outlined
          single-line
          :dense="dense"
          :value="fieldValue"
          :autofocus="autofocus"
          :disabled="disabled"
          :readonly="isReadOnly"
          :hide-details="hideDetails"
          :placeholder="$t(placeholder)"
          :type="inputType"
          :rules="rules"
          @keydown.enter="isSearch ? _clickSearch() : null"
          @keypress="_formatInput($event)"
          @input="_updateValue($event)"
          @blur="_onBlur"
          :prefix="$t(prefix)"
          :suffix="$t(suffix)"
          :class="inputClass"
          autocomplete="new-password"
          @click:clear="_onClear()"
          :clearable="enableClear"
          :hint="$t(hint)"
          :persistent-hint="hint !== ''"
          @click="$emit('field-click')"
          v-bind="attrs"
          v-on="on"
        >
          <template v-slot:append>
            <v-icon v-if="isSearch" @click.prevent="_clickSearch()">{{ icon.mdiMagnify }}</v-icon>
            <template v-if="type === 'password' && showPwView">
              <v-btn
                v-if="inputType === 'text'"
                icon
                text
                solo
                dense
                v-ripple="false"
                @click.prevent="inputType = 'password'"
              >
                <v-icon class="fi fi-rr-eye" size="20"></v-icon>
              </v-btn>
              <v-btn
                v-else-if="inputType === 'password'"
                icon
                text
                solo
                dense
                v-ripple="false"
                @click.prevent="inputType = 'text'"
              >
                <v-icon class="fi fi-rr-eye-crossed" size="20"></v-icon>
              </v-btn>
            </template>
            <slot name="append-inner"></slot>
          </template>
          <template v-slot:append-outer>
            <slot name="append-outer"></slot>
          </template>
          <template v-slot:prepend-inner>
            <slot name="prepend-inner"></slot>
          </template>
        </v-text-field>
      </template>
      <span>{{ $t(placeholder) }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mdiMagnify } from '@mdi/js'

export default {
  name: 'FormInput',
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    fieldValue: {
      type: String | Number,
      required: true,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    customRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    isIntegerOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isNotNumericOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEngNameOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isAlphaNumericOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isStrictFloat: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearchId: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    prefix: {
      type: String,
      required: false,
      default: '',
    },
    suffix: {
      type: String,
      required: false,
      default: '',
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    showPwView: {
      type: Boolean,
      required: false,
      default: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
    enableTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    rules() {
      let _rules = [v => (this.required ? this.$validate.required(v) || this.$t('message.formRequired') : true)]

      switch (this.type) {
        case 'number':
          if (this.isIntegerOnly) {
            _rules.push(v => this.gfCheckRule(this.$validate.isNumber(v, !this.required)))
          } else if (this.isStrictFloat) {
            _rules.push(v => this.gfCheckRule(this.$validate.isStrictFloating(v, !this.required)))
          } else {
            _rules.push(v => this.gfCheckRule(this.$validate.isFloating(v, !this.required)))
          }
          break
        case 'tel':
          _rules.push(v => this.gfCheckRule(this.$validate.isPhoneNumber(v, !this.required)))
          break
        case 'email':
          _rules.push(v => this.gfCheckRule(this.$validate.isEmail(v, !this.required)))
          break
        case 'time':
          _rules.push(v => this.gfCheckRule(this.$validate.isValidTime(v, !this.required)))
          break
        case 'password':
          _rules.push(v => this.gfCheckRule(this.$validate.isValidPassword(v, !this.required)))
          break
        default:
          if (this.isEngNameOnly) {
            _rules.push(v => this.gfCheckRule(this.$validate.isEngName(v, !this.required)))
          }
          if (this.isNotNumericOnly) {
            _rules.push(v => {
              if (!this.required && !this.$validate.DataValid(v)) return true
              return /^\d+$/.test(String(v).trim()) ? this.$t('message.notNumericOnly') : true
            })
          }
          break
      }

      _rules = _rules.concat(this.customRules)
      return _rules
    },
    isReadOnly() {
      return this.readonly || this.$store.getters.isLoading
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading,
        'pw-view': this.type === 'password' && this.showPwView,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
  },
  data: () => ({
    icon: {
      mdiMagnify,
    },
    searchText: '',
    inputType: 'text',
  }),
  watch: {
    type: {
      handler(val) {
        if (val === 'password') {
          this.inputType = 'password'
        }
      },
      immediate: true,
    },
  },
  methods: {
    _formatInput(event) {
      if (this.isIntegerOnly) {
        return this.$formatter.number(event)
      } else if (this.isSearchId) {
        return this.$formatter.id(event)
      } else if (this.isEngNameOnly) {
        return this.$formatter.engName(event)
      } else if (this.isAlphaNumericOnly) {
        return this.$formatter.alphaNumeric(event)
      } else if (this.type === 'tel') {
        return this.$formatter.phone(event)
      } else if (this.type === 'number') {
        return this.$formatter.floating(event)
      } else if (this.type === 'time') {
        return this.$formatter.time(event)
      }
    },
    _updateFieldValue(value = '') {
      this.searchText = value || ''
      this.$emit('update:fieldValue', this.searchText)
      this.$emit('changed', this.searchText)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
    _updateValue(value = '') {
      this._updateFieldValue(value)
      this.$emit('input-value', this.searchText)
    },
    _onClear() {
      this._updateFieldValue()
      this.$emit('clear-value', '')
    },
    _onBlur(ev) {
      this.$emit('onblur', ev)
    },
    _clickSearch() {
      this.$emit('search-click', this.searchText)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .pw-view .v-input__append-inner {
  margin: 0 !important;
}
</style>
